/**
 * users/{uid}/engineEvents/{autoId} — the engine's APPEND-ONLY event log.
 *
 * This log is the pilot's entire measurement instrument (brief §5):
 * completeness is a feature, not plumbing. It extends the drillEvents design
 * — state answers "where is this member now", only an immutable history can
 * answer "what did they do" — with one difference: the engine logs PER-ITEM
 * outcomes, not just sessions, because the pilot's questions (which stage
 * loses people, do misses cluster, does deep review predict retention) live
 * below the session grain. Rules enforce immutability (`update, delete: if
 * false`), not convention.
 *
 * Event vocabulary (v: 1) — `type` + flat payload fields:
 *   session_start    { queueSize, newCount, reviewCount, dueCount, resumed }
 *   item_outcome     { recId, kind: 'new'|'light'|'deep', attempts,
 *                      clean, stageBefore, stageAfter, hwmAfter }
 *   session_end      { completed, total, durationSec }
 *   session_dissolve { sessionDateKey, completed, total }   // silent next-day rule
 *   situation_complete { situationId }
 *   free_review_start / free_review_end { items, durationSec }
 *   clip_start / clip_end / playlist_complete — reserved for the clip layer;
 *     clip_end carries { playedSec, clipSec, seeks } so scrub-to-end is
 *     DETECTABLE post-hoc (ruled: no anti-scrub gate, but the data must be
 *     able to show it).
 *   arc_* — the V2 expression arc (Amendment 1), each riding its phase's
 *     patchExpression batch, never a separate write:
 *   arc_video_end        { recId, situationId, playedSec, seeks, reachedEnd,
 *                          durSec }   // watch telemetry; NEVER completion credit
 *   arc_learn_complete   { recId, situationId, dkMiss, czMiss, rcHints,
 *                          wrTries }
 *   arc_apply_submit     { recId, situationId, hasCore, wallCardId }
 *   arc_expression_complete { recId, situationId, next: 'expression'|'review' }
 *   review_* / return_* — the situation-phase instruments (EngineReviewView).
 *
 * Zero-item session_starts are logged deliberately — "opened and bounced" is
 * the drop-off signal; a log without it only describes people who engaged.
 * Every reader must filter on `type` and treat unknown types as forward
 * compatibility, never as corruption.
 *
 * Fail-soft: a lost event costs one row of analytics, never the member's
 * session. Nothing here may throw into the session path.
 */
import { db } from "@/firebaseConfig";
import firebase from "firebase/compat/app";
import { noteFailure } from "@/utils/silentFailure";

const ENGINE_EVENTS = "engineEvents";

/** Drop undefined values — Firestore rejects them and one bad field must not
 *  cost the whole event. */
function clean(payload) {
  const out = {};
  for (const [k, v] of Object.entries(payload || {})) {
    if (v !== undefined) out[k] = v;
  }
  return out;
}

export default {
  /**
   * Append one event.
   * @param {string} uid
   * @param {string} type       vocabulary above
   * @param {object} payload    flat fields for the type
   * @param {string} dateKey    member-local "YYYY-MM-DD" — the same key the
   *                            streak is gated on, so curves and streaks can
   *                            never disagree about a day
   * @returns {Promise<boolean>} written?
   */
  async log(uid, type, payload, dateKey) {
    if (!uid || !type) return false;
    try {
      await db.collection("users").doc(String(uid)).collection(ENGINE_EVENTS).add({
        v: 1,
        type,
        ...clean(payload),
        dateKey: dateKey ?? null,
        loggedAt: firebase.firestore.FieldValue.serverTimestamp(),
      });
      return true;
    } catch (e) {
      noteFailure("engineEventsService.log", e);
      return false;
    }
  },
};
